
import { NetlogicCatalog } from "@/services/NetLogic/catalog/NetLogicCatalog";
import { NetLogicQueue } from "@/services/NetLogic/NetLogicQueue";
import { Response } from "@konv/http";
import { v4 as uuid } from "uuid";
import { Logger } from "./Logger";

class NetLogicTimeoutError extends Error {
  constructor(timeout) {
    super(`O app ultrapassou o tempo limite(${timeout}) de resposta`);
  }
}

export class AndroidWebviewBridge {
  
  constructor() {
    this.pendingPromises = new Map();
  }

  /** @type {AndroidBridge} */
  static instance = null;

  get loaded() {
    const loaded = window?.ReactNativeWebView;
    return !!loaded;
  }

  /**
   *
   * @returns {AndroidWebviewBridge}
   */
  static async getInstance() {
    let instance = null;

    await navigator.locks.request("android_bridge_lock", async (lock) => {
      if (!AndroidWebviewBridge.instance) {
        AndroidWebviewBridge.instance = new AndroidWebviewBridge();;
        AndroidWebviewBridge.instance.initialize();

        console.log('AndroidWebviewBridge inicializado');

        instance = AndroidWebviewBridge.instance;
      } else {
        instance = AndroidWebviewBridge.instance;
      }
    });

    return instance;
  }

  initialize() {
    const listener = this.onMessageReceived.bind(this);

    window.document.addEventListener(
      "message",
      listener
    );
  }


  onMessageReceived(event) {
    const data = JSON.parse(event.data);
    const { messageId, type, action, ...message } = data;

    if (!messageId) {
      console.warn(
        `[AndroidBridge] ${new Date().toISOString()} Recebeu uma mensagem sem id`
      );
      return;
    }

    if (action === "request") {
      this.resolveMessage(messageId, message);
    }

    if (action === "catalogUpdate") {
      const queue = NetLogicQueue.getInstance();

      console.log(
        `[AndroidBridge] ${new Date().toISOString()} Recebeu um evento de atualização do cardápio`, message);

      Logger.info(`Recebeu uma mensagem do broadcast`, message);

      queue.push(async () => {
        try {
          const parsedMessage =  JSON.parse(message.payload || {});
          const parsedEvent = Object.entries(parsedMessage)[0];

          const netlogicCatalog = NetlogicCatalog.getInstance();

          const [ eventKey, eventTimestamp ] = parsedEvent || [];
          if (eventKey && eventKey === "MenuRefresh" && eventTimestamp) {
            await netlogicCatalog.update(eventTimestamp);
          } else {
            const fallbackTimestamp = new Date().getTime();
            await netlogicCatalog.update(fallbackTimestamp);
          }
        } catch (err) {
          console.log(err);
        }
      });
    }
  }

  resolveMessage(messageId, message) {
    if (this.pendingPromises.has(messageId)) {
      console.log(
        `[AndroidBridge] ${new Date().toISOString()} ${messageId} Mensagem resolvida`
      );
      const [resolver] = this.pendingPromises.get(messageId);

      this.pendingPromises.delete(messageId);

      resolver({ ...message, messageId });
    } else {
      console.warn(
        `[AndroidBridge] ${new Date().toISOString()} ${messageId} Mensagem já resolvida ou inválida`,
        messageId
      );
    }
  }

  sendMessageToReactNative({ action, payload, timeout = 120000 }) {
    return new Promise((resolve, reject) => {
      const messageId = `msg_${uuid()}`;
      const message = {
        action: action,
        payload: payload,
        messageId,
      };

      window.ReactNativeWebView.postMessage(JSON.stringify(message));

      this.pendingPromises.set(messageId, [resolve, reject]);

      if (timeout) {
        setTimeout(() => {
          if (this.pendingPromises.has(messageId)) {
            console.error(
              `[AndroidBridge] ${new Date().toISOString()} ${messageId} O app ultrapassou o tempo limite(${timeout}) de resposta`
            );

            const [resolver, rejector] = this.pendingPromises.get(messageId);

            this.pendingPromises.delete(messageId);

            rejector(new NetLogicTimeoutError(timeout));
          }
        }, timeout);
      }
    });
  }

  /**
   *
   * @param {Object} options
   * @param {string} options.url - URL da requisição
   * @param {string} options.method - Método da requisição
   * @param {Object<string, string>} options.headers - Headers da requisição
   * @param {Object<string, string>} options.query - Parâmetros da requisição
   * @param {Object} options.data - Dados da requisição
   * @returns {Promise<Response>} - Promise com o status code e o payload da resposta
   */
  async request({ url, method, headers = {}, query = {}, data = {} }) {
    if (!this.loaded) {
      const errorMessage = `[AndroidBridge] ${new Date().toISOString()} O bridge não foi carregado`;
      console.warn(errorMessage);
      return new Response(null, 500, [errorMessage]);
    }

    const requestParams = {
      url: url,
      method: method,
      headers: headers || {},
      query: query || {},
      data: data || {},
    };

    console.log(
      `[AndroidBridge] ${new Date().toISOString()} Enviando dados para o bridge: %s`,
      JSON.stringify(requestParams)
    );

    const bridgeResponse = await this.sendMessageToReactNative({
      action: "request",
      payload: requestParams,
    });

    const messageId = bridgeResponse?.messageId;
    const response = JSON.parse(bridgeResponse.payload);

    console.log(
      `[AndroidBridge] ${new Date().toISOString()} ${messageId} Recebeu dados do bridge: %s`,
      response.status
    );

    const responseStatus = response.status;
    const responseData = response.data;

    if (responseStatus >= 400) {
      const errorMessageSegments = [
        responseData?.errorCode,
        responseData?.errorMessage,
      ];
      const errorMessage =
        `${errorMessageSegments.filter(Boolean).join(" - ")}` ||
        "Erro na comunicação com o serviço de mesas";

      return new Response(responseData, 1000, [errorMessage]);
    }

    return new Response(responseData, 200, []);
  }

}
